import { createFileRoute, useNavigate } from "@tanstack/react-router";
import { z } from "zod";

import {
  InfiniteQueryPagination,
  useInfiniteQueryPagination,
} from "~/components/infinite-query-pagination";
import { PermissionGuard } from "~/components/permission-guard";
import { Skeleton } from "~/components/ui/skeleton";
import { betaWaitlistSignupsQuery } from "~/modules/beta-waitlist/queries";

export const Route = createFileRoute("/_dash/beta-waitlist")({
  validateSearch: z.object({
    page: z.number().int().positive().optional(),
  }),
  component: RouteComponent,
});

function RouteComponent() {
  return (
    <PermissionGuard permission="users:view">
      <BetaWaitlistPage />
    </PermissionGuard>
  );
}

function BetaWaitlistPage() {
  const { page } = Route.useSearch();
  const navigate = useNavigate({ from: Route.fullPath });
  const { data, fetchNextPage, hasNextPage, isFetchingNextPage, isLoading, isError } =
    betaWaitlistSignupsQuery.useInfiniteQuery({ params: {} });
  const {
    currentPageData,
    currentPageIndex,
    totalPages,
    onPageChange,
    onPreviousPage,
    onNextPage,
  } = useInfiniteQueryPagination({
    page,
    data,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
    navigateToPage: (nextPage) => {
      navigate({ search: { page: nextPage } });
    },
  });
  const totalCount = data?.pages[0]?.totalCount;
  const showPagination = totalPages > 0 && (hasNextPage || currentPageIndex > 0 || totalPages > 1);
  const showLoadingRows = isLoading || (isFetchingNextPage && !currentPageData);

  return (
    <div className="space-y-6">
      <div>
        {isLoading ? (
          <Skeleton className="h-8 w-44" />
        ) : (
          <p className="text-2xl font-semibold tabular-nums">
            {totalCount?.toLocaleString() ?? "-"} total signups
          </p>
        )}
      </div>

      <div className="overflow-hidden rounded-md border">
        <table className="w-full text-left text-sm">
          <thead className="border-b bg-muted/50">
            <tr>
              <th className="px-4 py-3 font-medium">Email</th>
              <th className="px-4 py-3 font-medium">Instagram handle</th>
            </tr>
          </thead>
          <tbody>
            {showLoadingRows &&
              Array.from({ length: 8 }, (_, index) => (
                <tr key={index} className="border-b last:border-b-0">
                  <td className="px-4 py-3">
                    <Skeleton className="h-5 w-64" />
                  </td>
                  <td className="px-4 py-3">
                    <Skeleton className="h-5 w-40" />
                  </td>
                </tr>
              ))}

            {!showLoadingRows &&
              currentPageData?.signups.page.map((signup) => (
                <tr key={signup.id} className="border-b last:border-b-0">
                  <td className="px-4 py-3 font-medium">{signup.email}</td>
                  <td className="px-4 py-3">
                    {signup.instagramHandle ? `@${signup.instagramHandle}` : "-"}
                  </td>
                </tr>
              ))}

            {!showLoadingRows && isError && (
              <tr>
                <td className="px-4 py-8 text-center text-destructive" colSpan={2}>
                  Could not load waitlist signups.
                </td>
              </tr>
            )}

            {!showLoadingRows && !isError && currentPageData?.signups.page.length === 0 && (
              <tr>
                <td className="px-4 py-8 text-center text-muted-foreground" colSpan={2}>
                  No waitlist signups found.
                </td>
              </tr>
            )}
          </tbody>
        </table>
      </div>

      {showPagination && (
        <div className="flex justify-end pb-8">
          <InfiniteQueryPagination
            onPreviousPage={onPreviousPage}
            onNextPage={onNextPage}
            onPageChange={onPageChange}
            currentPageIndex={currentPageIndex}
            hasNextPage={hasNextPage}
            isFetchingNextPage={isFetchingNextPage}
            totalPages={totalPages}
          />
        </div>
      )}
    </div>
  );
}
